import React, { useEffect, useState } from 'react';
import { Button, Space, message, Modal } from 'antd';
import CommonTable from 'components/CommonTable';
import type { ColumnsType } from 'antd/es/table';
import { ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { getTeamList, kiwiTeamDelete } from 'api/airtable';
import CreateAndEditPage from './components/CreateAndEditPage';
import type { TTeam } from 'types/kiwiHealth';
import s from './s.module.less';

const TeamManagementPage = () => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [teamItem, setTeamItem] = useState<TTeam>();
    const [listData, setListData] = useState<TTeam[]>([]);
    const [modal, contextHolder] = Modal.useModal();

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const res = await getTeamList();
            setListData(res);
        } catch (e) {
            console.error('error: ', e);
            setListData([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (record: TTeam) => {
        modal.confirm({
            width: '600px',
            closable: true,
            title: `Do you confirm removing this carousel? - ${record.name}?`,
            icon: <ExclamationCircleOutlined />,
            content: '',
            okText: 'Yes, confirm',
            cancelText: 'Cancel',
            onOk: async () => {
                const index = record?.index;
                try {
                    await kiwiTeamDelete(index).finally(() => fetchData());
                } catch (error) {
                    console.error(error);
                }
            },
        });
    };

    const handleAdd = () => {
        setIsEdit(false);
        setIsOpen(true);
        setTeamItem(undefined);
    };

    const handleEdit = (record: TTeam) => {
        setTeamItem(record);
        setIsEdit(true);
        setIsOpen(true);
    };

    const onSuccess = () => {
        setIsOpen(false);
        fetchData();
        message.success(isEdit ? 'Updated successfully' : 'Added successfully');
    };

    const columns: ColumnsType<TTeam> = [
        {
            title: 'NAME',
            dataIndex: 'name',
            key: 'name',
            align: 'left',
            width: '300px',
        },
        {
            title: 'JOB POSITION',
            dataIndex: 'position',
            key: 'position',
            align: 'left',
            width: '300px',
        },
        {
            title: 'DESCRIPTION',
            dataIndex: 'description',
            key: 'description',
            align: 'left',
            width: '300px',
        },
        {
            title: 'IMAGE (240 * 240)',
            dataIndex: 'image_url',
            key: 'image_url',
            align: 'left',
            width: '350px',
            render: (_, record: TTeam) => (
                <img className={s.banner} src={record.image_url} />
            ),
        },
        {
            title: '',
            align: 'center',
            render: (_, record: TTeam) => (
                <Space size="small">
                    <Button
                        className={s.link}
                        type="text"
                        onClick={() => handleEdit(record)}
                    >
                        Edit
                    </Button>
                    <Button
                        className={s.link}
                        type="text"
                        onClick={() => handleDelete(record)}
                    >
                        Remove
                    </Button>
                </Space>
            ),
        },
    ];

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <div className={s.wrap}>
            <div className={s.header}>
                <div className={s.title}>Manage your carousel</div>
                <Button onClick={handleAdd} icon={<PlusOutlined />}>
                    Add a new one
                </Button>
            </div>
            <CommonTable
                bordered
                loading={isLoading}
                columns={columns}
                data={listData}
            />
            {isOpen && (
                <CreateAndEditPage
                    item={teamItem}
                    onCancel={() => setIsOpen(false)}
                    onSuccess={onSuccess}
                />
            )}
            {contextHolder}
        </div>
    );
};

export default TeamManagementPage;
